import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Search, Star, Terminal } from "lucide-react";
import api from "@/lib/api";
import { SectionLabel } from "@/components/bits";
import VouchCard from "@/components/VouchCard";
import { DISCORD_URL } from "@/lib/images";

const FILTERS = [
  ["all", "ALL LOGS"],
  ["verified", "VERIFIED"],
  ["five", "RATING 5.0"],
];

export default function Vouches() {
  const [vouches, setVouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [freshIds, setFreshIds] = useState(new Set());
  const knownRef = useRef(new Set());

  // live feed: poll for new vouches; unseen ones decode in like a transmission
  useEffect(() => {
    let alive = true;
    const load = (initial) => api.get("/vouches?limit=1000").then((r) => {
      if (!alive) return;
      const incoming = r.data;
      if (initial) {
        incoming.forEach((v) => knownRef.current.add(v.message_id));
        setVouches(incoming);
        return;
      }
      const fresh = incoming.filter((v) => !knownRef.current.has(v.message_id));
      if (!fresh.length) return;
      fresh.forEach((v) => knownRef.current.add(v.message_id));
      setVouches(incoming);
      setFreshIds(new Set(fresh.map((v) => v.message_id)));
      setTimeout(() => alive && setFreshIds(new Set()), 8000);
    }).catch(() => {});
    setLoading(true);
    load(true).finally(() => alive && setLoading(false));
    const id = setInterval(() => load(false), 45000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const rated = vouches.filter((v) => v.rating != null);
  const avg = rated.length ? (rated.reduce((s, v) => s + v.rating, 0) / rated.length).toFixed(1) : "5.0";
  const fiveCount = vouches.filter((v) => v.rating === 5).length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vouches.filter((v) => {
      if (filter === "five" && v.rating !== 5) return false;
      if (!q) return true;
      return [v.author_name, v.content, v.product_slug].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [vouches, filter, query]);

  return (
    <div className="pt-32 pb-28 mx-auto max-w-[1400px] px-5 sm:px-8" data-testid="vouches-page">
      <SectionLabel>STRAIGHT FROM THE COMMUNITY</SectionLabel>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
        <h1 className="font-display text-5xl sm:text-7xl uppercase leading-none">Vouches</h1>
        <div className="flex items-center gap-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-4xl leading-none">{avg}</span>
              <Star size={20} className="text-white fill-white" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 mt-1">{vouches.length} REVIEWS</div>
          </div>
        </div>
      </div>
      <p className="mt-5 max-w-xl text-white/50">Live, unedited reviews pulled straight from our Discord vouch channel. Real customers, real results.</p>

      <section className="terminal-shell mt-14" data-testid="terminal-vouch-wall">
        <div className="relative z-10 border-b border-white/10 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.2em] text-white/60">
              <Terminal size={15} /> SYS://LOGS.VOUCHES_STREAM
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] text-white/70">
              <span className="h-2 w-2 rounded-full bg-white pulse-dot" /> LIVE_FEED: ENCRYPTED
            </div>
          </div>
          <div className="mt-5 grid gap-px rounded-xl overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3">
            <div className="bg-black px-4 py-3"><span className="font-mono text-[9px] tracking-[0.2em] text-white/35">TOTAL LOGS</span><div className="mt-1 font-display text-2xl">{vouches.length}</div></div>
            <div className="bg-black px-4 py-3"><span className="font-mono text-[9px] tracking-[0.2em] text-white/35">AVG RATING</span><div className="mt-1 font-display text-2xl">{avg}</div></div>
            <div className="bg-black px-4 py-3"><span className="font-mono text-[9px] tracking-[0.2em] text-white/35">5-STAR HITS</span><div className="mt-1 font-display text-2xl">{fiveCount}</div></div>
          </div>
          <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)} data-testid={`vouch-filter-${key}`}
                  className={`border px-4 py-2 rounded-full font-mono text-[10px] tracking-[0.16em] transition-colors ${filter === key ? "border-white bg-white text-black" : "border-white/15 text-white/45 hover:border-white/50 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
            <label className="flex min-w-0 items-center gap-3 rounded-full border border-white/12 px-4 py-2.5 lg:w-80">
              <Search size={14} className="shrink-0 text-white/35" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="SEARCH LOGS / PRODUCT"
                data-testid="vouch-search"
                className="w-full bg-transparent font-mono text-xs uppercase tracking-[0.12em] focus:outline-none placeholder:text-white/25" />
            </label>
          </div>
        </div>

        <div className="relative z-10 p-5 sm:p-6">
          {loading ? (
            <div className="py-24 text-center font-mono text-xs tracking-[0.2em] text-white/40">// CONNECTING TO DISCORD FEED...</div>
          ) : vouches.length === 0 ? (
            <div className="border border-white/10 p-12 text-center">
              <p className="text-white/50">No vouches synced yet.</p>
              <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="btn-inj mt-6 inline-flex px-8 py-3.5 text-sm">SEE THEM ON DISCORD</a>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center font-mono text-xs tracking-[0.2em] text-white/35" data-testid="vouches-empty-filter">// NO LOGS MATCH THAT FILTER</div>
          ) : (
            <div className="columns-1 gap-6 md:columns-2 xl:columns-3" data-testid="vouches-grid">
              {filtered.map((v) => <VouchCard key={v.message_id} v={v} fresh={freshIds.has(v.message_id)} />)}
            </div>
          )}
        </div>
      </section>

      <div className="mt-16 border border-white/15 p-10 text-center">
        <SectionLabel className="justify-center">JOIN THEM</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-5xl uppercase">Leave your own vouch</h2>
        <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="vouches-discord-btn" className="btn-inj mt-7 inline-flex items-center gap-2 px-8 py-4 text-sm">
          <MessageCircle size={16} /> discord.gg/injectd
        </a>
      </div>
    </div>
  );
}
